import VISP_HTTP from "@/visp-http";
import {
  buildEnvironmentUrl,
  buildListCvcsUrl,
  buildListHostsUrl,
  buildPreflightUrl,
  buildSetWindowUrl,
  buildPreAssignStaticsUrl,
  buildReleasePreAssignmentsUrl,
  buildIpChangeReportUrl,
  buildBuildOnTargetUrl,
  buildRepolicyUrl,
  buildVerifyTargetUrl,
  buildRemoveTargetCopiesUrl,
  buildShutdownSourceUrl,
  buildRollbackSourceUrl,
  buildRestoreOldIpsUrl,
  buildConfirmRepointUrl,
  buildActivateUrl,
  buildCommitStaticsUrl,
  buildKickAllUrl,
  buildVerifyReconnectedUrl,
  buildMarkMigratedUrl,
  buildRemoveSourceCopiesUrl,
  buildCancelMigrationUrl,
  buildStartRollbackUrl,
  buildRollbackWindowUrl,
  buildStopRollbackUrl,
  buildConfirmRepointBackUrl,
  buildMoveBackUrl,
  buildCloseRolledBackUrl,
  buildBuildOnSourceUrl,
  buildRepolicyOnSourceUrl,
  buildRemoveFromSourceUrl,
  errorMessage,
  errorSeverity,
  isAccessDenied,
} from "@/utils/cvcMigration";

// Vuex actions wrapping the highlander CVC-migration orchestrator endpoints.
// Each action is one call to request(); the URL builders in utils/cvcMigration
// stay the single source of truth for the paths.
//
// Named export shape is required. The auto-loader in
// Connect/src/store/modules/index.js does
//   { namespaced: true, ...moduleDefinition }
// which spreads the ES module namespace, so `actions` must be a top-level
// named export to land at the top of the Vuex module. A `export default
// { actions }` here would nest actions under `default` and Vuex would
// register the namespace with zero actions - every dispatch silently
// returns undefined.

const DEFAULT_TIMEOUT = 45000;
// A server-side loop over every service of the CVC.
const LOOP_TIMEOUT = 120000;
// One verify call; Usher gives up on a router session after 30 s.
const VERIFY_TIMEOUT = 40000;
// One kick call: VispApi ends a slice by about 50 s and the proxy cuts at 60 s.
const KICK_TIMEOUT = 55000;
// A rollback or a restore writes the old IPs one static at a time before it
// answers; the proxy cuts at 60 s.
const ROLLBACK_TIMEOUT = 55000;
const TOAST_TIMEOUT = 10000;

// The one place that talks to VispApi. loader: global "Processing" modal
// (single-shot actions only; loops and polls show their own progress).
// silent: no toast, for polls. quietDenied: no toast for an access denied
// answer, because the page shows its own alert for it. The error is always
// rethrown.
// A POST always carries a body: with no body and no Content-Type the API answers
// 415, and axios drops the Content-Type header when data is undefined.
async function request (dispatch, { method = "post", url, data, params, loader = true, silent = false, quietDenied = false, timeout = DEFAULT_TIMEOUT }) {
  if (loader) dispatch("processLoader/setProcessActive", {}, { root: true });
  try {
    const body = String(method).toLowerCase() === "post" && data === undefined ? {} : data;
    const res = await VISP_HTTP.request({ method, url, data: body, params, timeout });
    return res.data;
  } catch (e) {
    if (!silent && !(quietDenied && isAccessDenied(e))) {
      dispatch(
        "httpResponse/setHttpResponse",
        { text: errorMessage(e), color: errorSeverity(e), timeout: TOAST_TIMEOUT },
        { root: true }
      );
    }
    throw e;
  } finally {
    if (loader) dispatch("processLoader/setProcessInactive", {}, { root: true });
  }
}

const withIds = (key, ids) => (ids ? { [key]: ids } : {});

export const actions = {
  cvcMigrationEnvironment ({ dispatch }) {
    return request(dispatch, { method: "get", url: buildEnvironmentUrl(), loader: false, quietDenied: true });
  },

  cvcMigrationListCvcs ({ dispatch }) {
    return request(dispatch, { method: "get", url: buildListCvcsUrl(), loader: false, quietDenied: true });
  },

  cvcMigrationListHosts ({ dispatch }) {
    return request(dispatch, { method: "get", url: buildListHostsUrl(), loader: false, quietDenied: true });
  },

  cvcMigrationPreflight ({ dispatch }, { cvcId, targetHostId }) {
    return request(dispatch, { url: buildPreflightUrl(cvcId, targetHostId) });
  },

  // The page poll: no modal, no toast, and the server writes no audit row.
  cvcMigrationRefresh ({ dispatch }, { cvcId, targetHostId }) {
    return request(dispatch, {
      url: buildPreflightUrl(cvcId, targetHostId, { refresh: true }),
      loader: false,
      silent: true,
    });
  },

  cvcMigrationSetWindow ({ dispatch }, { cvcId, targetHostId, startUtc, endUtc }) {
    return request(dispatch, { url: buildSetWindowUrl(cvcId, targetHostId), data: { startUtc, endUtc } });
  },

  cvcMigrationPreAssignStatics ({ dispatch }, { cvcId, targetHostId }) {
    return request(dispatch, { url: buildPreAssignStaticsUrl(cvcId, targetHostId), timeout: LOOP_TIMEOUT });
  },

  cvcMigrationReleasePreAssignments ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildReleasePreAssignmentsUrl(cvcId), timeout: LOOP_TIMEOUT });
  },

  cvcMigrationIpChangeReport ({ dispatch }, { cvcId, targetHostId }) {
    return request(dispatch, { method: "get", url: buildIpChangeReportUrl(cvcId, targetHostId) });
  },

  cvcMigrationBuild ({ dispatch }, { cvcId, targetHostId, avcIds }) {
    return request(dispatch, {
      url: buildBuildOnTargetUrl(cvcId, targetHostId),
      data: withIds("avcIds", avcIds),
      timeout: LOOP_TIMEOUT,
    });
  },

  cvcMigrationRepolicy ({ dispatch }, { cvcId, avcIds }) {
    return request(dispatch, { url: buildRepolicyUrl(cvcId), data: { avcIds }, timeout: LOOP_TIMEOUT });
  },

  // One chunk of the page's verify loop: the loop shows its own progress.
  cvcMigrationVerifyTargetChunk ({ dispatch }, { cvcId, targetHostId, avcIds }) {
    return request(dispatch, {
      url: buildVerifyTargetUrl(cvcId, targetHostId),
      data: withIds("avcIds", avcIds),
      loader: false,
      timeout: VERIFY_TIMEOUT,
    });
  },

  cvcMigrationRemoveTargetCopies ({ dispatch }, { cvcId, copyIds, force = false }) {
    return request(dispatch, { url: buildRemoveTargetCopiesUrl(cvcId), data: { copyIds, force } });
  },

  cvcMigrationShutdownSource ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildShutdownSourceUrl(cvcId) });
  },

  // `unshutWithoutRestore` brings the source back without putting the old IPs
  // back; VispApi accepts it only while RADIUS cannot be read.
  cvcMigrationRollbackSource ({ dispatch }, { cvcId, unshutWithoutRestore = false }) {
    return request(dispatch, {
      url: buildRollbackSourceUrl(cvcId),
      data: unshutWithoutRestore ? { unshutWithoutRestore: true } : undefined,
      timeout: ROLLBACK_TIMEOUT,
    });
  },

  // The old IPs of the committed statics back into RADIUS and the pool; no router change.
  cvcMigrationRestoreOldIps ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildRestoreOldIpsUrl(cvcId), timeout: ROLLBACK_TIMEOUT });
  },

  cvcMigrationConfirmRepoint ({ dispatch }, { cvcId, confirmedBy, sourceShutFailed = false }) {
    return request(dispatch, { url: buildConfirmRepointUrl(cvcId), data: { confirmedBy, sourceShutFailed } });
  },

  cvcMigrationActivate ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildActivateUrl(cvcId) });
  },

  cvcMigrationCommitStatics ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildCommitStaticsUrl(cvcId), timeout: LOOP_TIMEOUT });
  },

  // One call of a server-owned kick run: the first call starts it (ids, run id,
  // kickAll), every later call continues it. The page shows its own progress.
  cvcMigrationKick ({ dispatch }, { cvcId, radiusServiceIds, runId, continueRun, kickAll, restored }) {
    return request(dispatch, {
      url: buildKickAllUrl(cvcId),
      data: {
        ...withIds("radiusServiceIds", radiusServiceIds),
        ...(runId ? { runId } : {}),
        ...(continueRun ? { continue: true } : {}),
        ...(kickAll === undefined ? {} : { kickAll: !!kickAll }),
        ...(restored ? { restored: true } : {}),
      },
      loader: false,
      silent: true,
      timeout: KICK_TIMEOUT,
    });
  },

  // The state of the latest kick run of the CVC (no audit row, no Usher call).
  cvcMigrationKickRun ({ dispatch }, { cvcId }) {
    return request(dispatch, { method: "get", url: buildKickAllUrl(cvcId), loader: false, silent: true });
  },

  cvcMigrationVerifyReconnected ({ dispatch }, { cvcId, refresh }) {
    return request(dispatch, {
      url: buildVerifyReconnectedUrl(cvcId, { refresh }),
      loader: !refresh,
      silent: !!refresh,
    });
  },

  cvcMigrationMarkMigrated ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildMarkMigratedUrl(cvcId) });
  },

  cvcMigrationRemoveSourceCopies ({ dispatch }, { cvcId, agreedBy }) {
    return request(dispatch, { url: buildRemoveSourceCopiesUrl(cvcId), data: { agreedBy } });
  },

  cvcMigrationCancel ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildCancelMigrationUrl(cvcId) });
  },

  // The rollback after the re-point confirm: each call is one step of the page.
  // The window is UTC, or all three fields say "not booked yet".
  cvcMigrationStartRollback ({ dispatch }, { cvcId, reason, agreedBy, windowStartUtc, windowEndUtc, windowNotBooked }) {
    return request(dispatch, {
      url: buildStartRollbackUrl(cvcId),
      data: { reason, agreedBy, windowStartUtc, windowEndUtc, windowNotBooked },
    });
  },

  cvcMigrationRollbackWindow ({ dispatch }, { cvcId, windowStartUtc, windowEndUtc, windowNotBooked }) {
    return request(dispatch, { url: buildRollbackWindowUrl(cvcId), data: { windowStartUtc, windowEndUtc, windowNotBooked } });
  },

  cvcMigrationStopRollback ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildStopRollbackUrl(cvcId) });
  },

  cvcMigrationConfirmRepointBack ({ dispatch }, { cvcId, confirmedBy, builtByNetwork = false }) {
    return request(dispatch, { url: buildConfirmRepointBackUrl(cvcId), data: { confirmedBy, builtByNetwork: !!builtByNetwork } });
  },

  cvcMigrationMoveBack ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildMoveBackUrl(cvcId) });
  },

  cvcMigrationCloseRolledBack ({ dispatch }, { cvcId }) {
    return request(dispatch, { url: buildCloseRolledBackUrl(cvcId) });
  },

  // A server-side loop over the AVCs; no ids means the server's own list.
  cvcMigrationBuildOnSource ({ dispatch }, { cvcId, avcIds }) {
    return request(dispatch, { url: buildBuildOnSourceUrl(cvcId), data: withIds("avcIds", avcIds), timeout: LOOP_TIMEOUT });
  },

  cvcMigrationRepolicyOnSource ({ dispatch }, { cvcId, avcIds }) {
    return request(dispatch, { url: buildRepolicyOnSourceUrl(cvcId), data: { avcIds }, timeout: LOOP_TIMEOUT });
  },

  cvcMigrationRemoveFromSource ({ dispatch }, { cvcId, copyIds }) {
    return request(dispatch, { url: buildRemoveFromSourceUrl(cvcId), data: withIds("copyIds", copyIds) });
  },
};
